import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';
import { clientViewSessionId, rememberClientView, clearClientView, CLIENT_VIEW_ERROR_EVENT, CLIENT_VIEW_GLOBAL_KEY } from '../utils/clientViewAs';
import { isMaintenanceError } from '../utils/maintenance';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewAs, setViewAs] = useState(null);
  const [viewAsError, setViewAsError] = useState('');

  useEffect(() => {
    const onError = (event) => setViewAsError(event.detail || 'The client view is no longer available.');
    const onStorage = (event) => {
      if (event.key === CLIENT_VIEW_GLOBAL_KEY) setViewAsError('The client view changed in another tab. End the view before continuing.');
    };
    window.addEventListener(CLIENT_VIEW_ERROR_EVENT, onError);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CLIENT_VIEW_ERROR_EVENT, onError);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.get('/auth/me', { withCredentials: true })
        .then(res => {
          if (res.data.viewAs?.scope === 'client') {
            rememberClientView(res.data.viewAs);
            setViewAs(res.data.viewAs);
          } else if (clientViewSessionId()) {
            setViewAsError('This client view has ended. End the view to return to your admin account.');
            return;
          }
          setUser(res.data.data);
        })
        .catch((error) => {
          if (clientViewSessionId() || error.response?.data?.code?.startsWith('CLIENT_VIEW_AS_')) {
            setViewAsError('The client view could not be verified. End the view before continuing.');
          } else if (!isMaintenanceError(error)) {
            localStorage.removeItem('token');
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('token', res.data.data.token);
    setUser(res.data.data.user);
    return res.data;
  };

  const register = async (data) => {
    const res = await api.post('/auth/register', data);
    localStorage.setItem('token', res.data.data.token);
    setUser(res.data.data.user);
    return res.data;
  };

  const logout = async () => {
    const token = localStorage.getItem('token');
    let revoked = !token;

    try {
      if (token) {
        await api.post('/auth/logout', {}, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
          financeViewAs: Boolean(sessionStorage.getItem('financeViewAsSessionId'))
        });
        revoked = true;
      }
    } catch (error) {
      revoked = false;
    } finally {
      // Always finish the local logout, but only after the revocation request settles.
      localStorage.removeItem('token');
      clearClientView();
      setViewAs(null);
      setViewAsError('');
      setUser(null);
    }

    return revoked;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUser, viewAs, viewAsError }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
